import { forwardRef, type RefObject, useImperativeHandle, useRef } from 'react';
import { VStack } from '@direct-frontend/components';
import { type TestProps } from '@direct-frontend/components/testing';
import { type BaseRowData, type DataProvider } from '@adv-frontend/data-provider';
import { type GridHandle, type GridRef } from '@adv-frontend/grid';
import { useAsyncFlow } from '@adv-frontend/modules-flow';
import { type GridMobileRenderItem, type GridMobileRenderItemStub } from './lib/types';
import { GridMobileRow } from './GridMobileRow';

const EMPTY_ROWS_IDS: string[] = [];

export type GridMobileProps<RowData extends BaseRowData> = TestProps & {
    /** Flow-based провайдер данных строк */
    dataProvider: DataProvider<RowData>;
    /** Рендерер загруженной строки */
    renderItem: GridMobileRenderItem<RowData>;
    /** Рендерер стаба строки на время загрузки */
    renderItemStub: GridMobileRenderItemStub;
    /**
     * Элемент, к которому скроллим при смене страницы.
     * Если не передан — скролл к корню грида.
     */
    scrollAnchorRef?: RefObject<HTMLElement | null>;
};

function GridMobileInner<RowData extends BaseRowData>(
    {
        'data-testid': dataTestId = 'GridMobile',
        dataProvider,
        renderItem,
        renderItemStub,
        scrollAnchorRef,
    }: GridMobileProps<RowData>,
    ref: GridRef
) {
    const rootRef = useRef<HTMLDivElement>(null);

    const rowsIdsResult = useAsyncFlow(dataProvider.rowsIds);

    /**
     * До готовности списка виртуальных строк рисуем пустой список, не суспендимся.
     * SubRows на мобилке рендерятся внутри родительской карточки, поэтому используются
     * только корневые виртуальные id.
     */
    const rowsIds = rowsIdsResult.isReady || rowsIdsResult.isRefreshing ? rowsIdsResult.current() : EMPTY_ROWS_IDS;

    useImperativeHandle(ref, (): GridHandle => {
        return {
            scrollCellToIndex: () => undefined,
            scrollRowToIndex: () => undefined,
            scrollToOffset: () => {
                (scrollAnchorRef?.current ?? rootRef.current)?.scrollIntoView({ block: 'start' });
            },
            getFirstVisibleRowIndex: () => 0,
            isScrollableByY: () => false,
            horizontalScrollToOffset: () => undefined,
            isScrolledToEndByY: () => true,
            getGridDimensions: () => {
                return {
                    height: rootRef.current?.offsetHeight ?? 0,
                    width: rootRef.current?.offsetWidth ?? 0,
                };
            },
        };
    }, [scrollAnchorRef]);

    return (
        <div data-testid={dataTestId} ref={rootRef}>
            <VStack gap="8">
                {rowsIds.map((rowId, listIndex) => (
                    <GridMobileRow
                        key={rowId}
                        rowId={rowId}
                        listIndex={listIndex}
                        dataProvider={dataProvider}
                        renderItem={renderItem}
                        renderItemStub={renderItemStub}
                    />
                ))}
            </VStack>
        </div>
    );
}

export const GridMobile = forwardRef(GridMobileInner) as <RowData extends BaseRowData>(
    props: GridMobileProps<RowData> & { ref?: GridRef }
) => ReturnType<typeof GridMobileInner>;
